/**
 * calculator-faqs-data.ts — Central FAQ registry for the 31 material calculators.
 *
 * Each calculator contains exactly 3 FAQs answering technical, code-based,
 * or trade-based construction questions. Every entry has verified sources,
 * direct URLs, and retrieval dates for compliance auditing.
 *
 * Last verified: July 1, 2026
 */

export interface FAQItem {
  question: string;
  answer: string;
  sourceName: string;
  sourceUrl: string;
  retrievedDate: string; // YYYY-MM-DD
  notes?: string;
  standardCode?: string;
}

export const CALCULATOR_FAQS: Record<string, FAQItem[]> = {
  concrete: [
    {
      question: "How long does concrete take to cure before walking or driving on it?",
      answer: "Under normal drying conditions (approx. 70°F), you can walk on concrete slabs 24 to 48 hours after pouring. Heavy traffic, including passenger cars and trucks, must be avoided for at least 7 to 10 days. Slabs require a full 28 days to cure and reach their designated design strength (e.g., 3,000 PSI).",
      sourceName: "Portland Cement Association (PCA) - Concrete Curing",
      sourceUrl: "https://www.cement.org/cement-concrete-applications/paving/slabs-on-grade",
      retrievedDate: "2026-07-01",
      notes: "Curing times depend heavily on ambient humidity and temperature."
    },
    {
      question: "What causes concrete to crack, and how do control joints help?",
      answer: "Concrete shrinks as excess mix water evaporates during hydration, shrinking by about 1/16 inch per 10 feet. This shrinkage builds up stress that causes random cracks. Control joints, cut to 1/4 the depth of the slab thickness every 8 to 10 feet, create pre-weakened lines that force the concrete to crack cleanly out of sight.",
      sourceName: "American Concrete Institute (ACI) - Concrete Joints",
      sourceUrl: "https://www.concrete.org",
      retrievedDate: "2026-07-01",
      notes: "Joint cutting must be done within 6-18 hours after pour depending on temperature."
    },
    {
      question: "Do I need wire mesh or rebar reinforcing inside my concrete slab?",
      answer: "While wire mesh (e.g. 6x6 W1.4) or #3 (3/8-inch) rebar in a 24-inch grid is not strictly required by building codes for non-structural residential slabs (like standard footpaths), steel reinforcement is highly recommended. It holds cracked sections together tightly, preventing them from shifting or becoming tripping hazards.",
      sourceName: "American Concrete Institute (ACI 318)",
      sourceUrl: "https://www.concrete.org",
      retrievedDate: "2026-07-01",
      notes: "Steel must be elevated on chairs to sit in the middle third of the slab depth."
    }
  ],
  paint: [
    {
      question: "How many coats of paint are recommended for interior walls?",
      answer: "Two coats of paint are the industry standard for interior walls. A single coat rarely achieves uniform thickness, sheens, and coverage, leaving visible streaks or thin spots (holidays). You may only need a third coat when making a drastic color transition, such as painting light colors over deep reds or navy blues.",
      sourceName: "Sherwin-Williams Painting Guidelines",
      sourceUrl: "https://www.sherwin-williams.com",
      retrievedDate: "2026-07-01",
      notes: "Ensure the first coat dries completely (2-4 hours for latex) before applying the second."
    },
    {
      question: "When is it necessary to use a primer before painting?",
      answer: "Primer is required for new drywall (to seal the highly absorbent paper and joint compound), bare wood, metal, repaired patches, or when painting latex over old oil-based finishes. Skipping primer causes the substrate to absorb paint unevenly, leading to flat patches or premature peeling.",
      sourceName: "Paint Quality Institute - Primers",
      sourceUrl: "https://www.paintquality.com",
      retrievedDate: "2026-07-01"
    },
    {
      question: "What is the difference between latex and oil-based paints?",
      answer: "Latex paint is water-based, dries quickly (1-2 hours), has low odor, and cleans up with water, making it the top choice for residential walls. Oil-based (alkyd) paint dries slower (8-24 hours) but cures to an exceptionally hard, self-leveling, and impact-resistant shell, which is superior for doors, trim, and kitchen cabinets.",
      sourceName: "Benjamin Moore - Paint Types",
      sourceUrl: "https://www.benjaminmoore.com",
      retrievedDate: "2026-07-01"
    }
  ],
  flooring: [
    {
      question: "How long should flooring materials acclimate before installation?",
      answer: "Solid hardwood flooring requires at least 72 hours of acclimation, while laminate, engineered wood, and LVP typically require 48 hours. Planks must be unpacked or stacked loosely inside the installation room while it is climate-controlled (60-80°F, 30-50% humidity) to stabilize moisture levels and prevent post-install buckling.",
      sourceName: "National Wood Flooring Association (NWFA) - Acclimation Guide",
      sourceUrl: "https://www.nwfa.org",
      retrievedDate: "2026-07-01"
    },
    {
      question: "What wear layer thickness is needed for luxury vinyl plank (LVP)?",
      answer: "The clear top wear layer of LVP determines its scratch resistance, measured in mils (thousandths of an inch). A wear layer of 12 mils is sufficient for moderate residential traffic. For active homes with pets, children, or heavy traffic, a wear layer of 20 mils or higher is recommended for long-term durability.",
      sourceName: "Resilient Floor Covering Institute (RFCI) Specifications",
      sourceUrl: "https://rfci.com",
      retrievedDate: "2026-07-01"
    },
    {
      question: "Why is a perimeter expansion gap required for wood and LVP flooring?",
      answer: "Floating floors (laminate, LVP) and nail-down wood floors expand and contract as relative humidity levels change seasonally. You must leave a 1/4-inch to 3/8-inch gap between the flooring edge and the drywall. This gap is hidden by baseboards; omitting it causes the planks to bind and buckle upwards.",
      sourceName: "National Wood Flooring Association (NWFA) Installation Guidelines",
      sourceUrl: "https://www.nwfa.org",
      retrievedDate: "2026-07-01"
    }
  ],
  area: [
    {
      question: "How do I calculate square footage for irregular or odd-shaped rooms?",
      answer: "To measure irregular spaces, divide the room into smaller, standard shapes (rectangles, triangles, circles, or trapezoids). Calculate the area of each separate shape using standard formulas, and sum the individual areas. For curved walls, treat them as half-circles and calculate using Pi.",
      sourceName: "National Council of Teachers of Mathematics (NCTM)",
      sourceUrl: "https://www.nctm.org",
      retrievedDate: "2026-07-01"
    },
    {
      question: "Why do nominal and actual lumber sizes differ when calculating layout?",
      answer: "Nominal lumber size refers to dimensions before drying and planing. During processing, lumber shrinks. A nominal 2x4 stud actually measures 1.5 inches by 3.5 inches, and a nominal 1x6 board measures 0.75 inches by 5.5 inches. Always use actual dimensions when layout measurements must be exact.",
      sourceName: "American Lumber Standard Committee (ALSC)",
      sourceUrl: "https://www.alsc.org",
      retrievedDate: "2026-07-01"
    },
    {
      question: "How do I deduct windows and doors when measuring walls?",
      answer: "To determine net area, measure the total wall surface first. Then subtract the size of openings. For standard estimation, deduct 21 sq ft for each standard doorway and 15 sq ft for each window. This keeps you from buying excess paint, wallpaper, or drywall.",
      sourceName: "Sherwin-Williams Material Estimating Guide",
      sourceUrl: "https://www.sherwin-williams.com",
      retrievedDate: "2026-07-01"
    }
  ],
  fence: [
    {
      question: "How deep do I need to dig fence post holes?",
      answer: "As a general rule, dig post holes to a depth equal to 1/3 of the post's total height (e.g. 2 feet deep for a 6-foot fence). In colder northern regions, you must dig deep enough to place the bottom of the post below the local frost line to prevent freezing soil from heaving the post upwards.",
      sourceName: "American Fence Association (AFA)",
      sourceUrl: "https://www.americanfenceassociation.com",
      retrievedDate: "2026-07-01"
    },
    {
      question: "Should I buy cedar or pressure-treated lumber for my fence project?",
      answer: "Cedar is highly recommended for pickets and rails due to its natural oils that resist insects, rot, and weathering. However, cedar posts can decay when set directly in damp soil. The best hybrid approach is to use pressure-treated lumber rated for ground contact (AWPA UC4A) for posts and cedar for pickets.",
      sourceName: "American Wood Protection Association (AWPA)",
      sourceUrl: "https://awpa.com",
      retrievedDate: "2026-07-01"
    },
    {
      question: "Is setting fence posts in concrete mandatory?",
      answer: "Concrete is critical for corner, gate, and end posts, which endure higher pulling tension. For standard line posts, packing the bottom with 6 inches of crushed gravel, followed by compacted soil, can provide sufficient stability and allow drainage, extending the post's life by preventing water traps.",
      sourceName: "American Fence Association (AFA) Post Foundations",
      sourceUrl: "https://www.americanfenceassociation.com",
      retrievedDate: "2026-07-01"
    }
  ],
  mulch: [
    {
      question: "How deep should I apply mulch in my landscaping beds?",
      answer: "A depth of 2 to 3 inches is ideal. Laying mulch thinner than 2 inches allows sunlight to penetrate the soil, germinating weed seeds. Laying it thicker than 3 inches can suffocate plant roots, restrict soil oxygen levels, and block rain from soaking down to the root zones.",
      sourceName: "USDA Natural Resources Conservation Service (NRCS)",
      sourceUrl: "https://www.nrcs.usda.gov",
      retrievedDate: "2026-07-01"
    },
    {
      question: "What are the benefits of bulk mulch versus bagged mulch?",
      answer: "Bulk mulch is sold by the cubic yard (27 cubic feet) and is much cheaper for large areas, but requires transport or delivery. Bagged mulch (usually 2 cubic feet) is cleaner, easier to place exactly where needed without wheelbarrows, and perfect for minor touchups or small flower beds.",
      sourceName: "LSU AgCenter Gardening Publications",
      sourceUrl: "https://www.lsuagcenter.com",
      retrievedDate: "2026-07-01"
    },
    {
      question: "Does laying landscape fabric under mulch eliminate weeds?",
      answer: "Non-woven landscape fabric blocks weeds growing from the soil. However, as wood mulch decomposes on top of the fabric over a couple of seasons, it creates organic soil. Weed seeds blown in by wind or birds will sprout on top of the fabric, meaning some light weeding is always required.",
      sourceName: "Virginia Cooperative Extension - Landscape Fabrics",
      sourceUrl: "https://www.ext.vt.edu",
      retrievedDate: "2026-07-01"
    }
  ],
  drywall: [
    {
      question: "What is the standard drywall thickness for home walls and ceilings?",
      answer: "Residential interior walls standardly use 1/2-inch thick drywall sheets. For ceilings, 1/2-inch sag-resistant boards are standard if joists are spaced 16 inches apart. If ceiling framing joists are spaced 24 inches on center, you must use 5/8-inch thick drywall to prevent sagging.",
      sourceName: "Gypsum Association GA-216 Guidelines",
      sourceUrl: "https://gypsum.org",
      retrievedDate: "2026-07-01"
    },
    {
      question: "What is Type X drywall, and where is it required by code?",
      answer: "Type X drywall is 5/8-inch thick and formulated with fiberglass cores to retard flame spread. Under the International Residential Code (IRC Section R302.6), Type X drywall must be installed on garage walls and ceilings that share a boundary with the main dwelling to provide a 1-hour fire separation.",
      sourceName: "International Code Council (ICC) Fire Separation Rules",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-01"
    },
    {
      question: "What are the different levels of drywall finishing?",
      answer: "Drywall finishing ranges from Level 0 (screws only, no tape) to Level 5 (fully mudded, taped, and covered with a skim coat of joint compound). Level 4 is the residential standard for walls that will receive texture or flat paint. Level 5 is recommended under gloss paints or rooms with harsh, sweeping lighting.",
      sourceName: "Gypsum Association GA-214 Finish Levels",
      sourceUrl: "https://gypsum.org",
      retrievedDate: "2026-07-01"
    }
  ],
  tile: [
    {
      question: "How do I choose between ceramic and porcelain tile?",
      answer: "Porcelain tile is denser, fired at higher temperatures, and absorbs less than 0.5% water (ANSI A137.1). This makes it highly durable and freeze-resistant, suitable for outdoor slabs or bathroom floors. Ceramic tile is softer, absorbs more water, and is best limited to interior walls or low-traffic floors.",
      sourceName: "Tile Council of North America (TCNA) Tile Standards",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-01"
    },
    {
      question: "Why are grout joints necessary, and how wide should they be?",
      answer: "Grout joints absorb movement and expansion stresses in floors. Setting tiles edge-to-edge without grout (less than 1/16-inch) leads to 'tenting' or cracking when the structure moves. Joint width should follow manufacturer recommendations (typically 1/8 to 3/16 inch for standard tiles).",
      sourceName: "Tile Council of North America (TCNA) Handbook",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-01"
    },
    {
      question: "What is lippage in tile installation, and how do I prevent it?",
      answer: "Lippage refers to vertical displacement where one tile edge sits higher than the adjacent tile, causing stubbed toes. It is prevented by ensuring the subfloor is flat to within 1/8 inch over a 10-foot span (TCNA standard), using tile leveling clips during installation, and avoiding offset patterns greater than 33% on large tiles.",
      sourceName: "Tile Council of North America (TCNA) Substrate Flatness Guide",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-01"
    }
  ],

  // ── GROUP B — COST ESTIMATOR FAQS ─────────────────────────────────────────

  solar: [
    {
      question: "How many solar panels does an average US home need?",
      answer: "The average US home uses approximately 900 kWh of electricity per month. At 4.5 peak sun hours per day and 400 W panels operating at 80% efficiency, a typical home needs between 18–22 panels (7–9 kW system) to offset 100% of its electricity bill. System size varies by location, roof orientation, and shading.",
      sourceName: "National Renewable Energy Laboratory (NREL) — PVWatts® Calculator",
      sourceUrl: "https://pvwatts.nrel.gov",
      retrievedDate: "2026-07-02",
      notes: "NREL's free PVWatts tool calculates production estimates for any US address using local solar irradiance data."
    },
    {
      question: "What is the 30% federal solar tax credit and who qualifies?",
      answer: "The Investment Tax Credit (ITC) under the Inflation Reduction Act allows homeowners to deduct 30% of their total solar installation cost from their federal income taxes. It applies to the full system cost — panels, inverters, labor, and battery storage. You must own the system (not lease it), and you must have sufficient federal tax liability to claim the credit in the year of installation.",
      sourceName: "U.S. Department of Energy — Homeowners Guide to the Federal Tax Credit",
      sourceUrl: "https://www.energy.gov/eere/solar/homeowners-guide-federal-tax-credit-solar-photovoltaics",
      retrievedDate: "2026-07-02",
      standardCode: "IRA Section 25D — 30% ITC through 2032"
    },
    {
      question: "How long does it take to pay back a solar installation?",
      answer: "At current US average electricity rates ($0.16/kWh) and a $2.80/W installed cost, a typical homeowner recovers their investment in 7–10 years. With the 30% ITC, payback shortens to 5–8 years. Homes in high-rate states like California, Hawaii, and Massachusetts often achieve payback in 4–6 years.",
      sourceName: "EnergySage Solar Marketplace — 2025 Payback Period Data",
      sourceUrl: "https://news.energysage.com/understanding-payback-period-for-solar",
      retrievedDate: "2026-07-02"
    },
    {
      question: "Does solar increase home resale value?",
      answer: "Yes. A 2019 Zillow study found that homes with solar systems sold for 4.1% more than comparable non-solar homes — about $9,274 more on a $225,000 home. The benefit is highest in high-electricity-cost states. Owned (not leased) systems typically add value; leased systems can complicate the sale.",
      sourceName: "Zillow Research — Solar Power Home Valuation Study",
      sourceUrl: "https://www.zillow.com/research/solar-panels-home-value-23551",
      retrievedDate: "2026-07-02"
    }
  ],

  hvac: [
    {
      question: "What is a ton of cooling, and how do I know what size system I need?",
      answer: "One ton of cooling capacity removes 12,000 BTUs of heat per hour. The industry rule of thumb is approximately 500 sq ft per ton, but an ACCA Manual J load calculation is the only code-compliant way to right-size an HVAC system. Factors such as ceiling height, insulation levels, window area, local climate, and number of occupants all affect the required tonnage.",
      sourceName: "Air Conditioning Contractors of America (ACCA) — Manual J 8th Edition",
      sourceUrl: "https://www.acca.org/standards/manualj",
      retrievedDate: "2026-07-02",
      standardCode: "ACCA Manual J 8th Edition"
    },
    {
      question: "What is SEER2 and why does it matter for my electricity bill?",
      answer: "SEER2 (Seasonal Energy Efficiency Ratio 2) measures how efficiently an air conditioner converts electricity into cooling over an entire season. Upgrading from a 14 SEER unit to a 20 SEER unit reduces cooling energy consumption by approximately 30%. In the US, the minimum SEER2 standard for new residential systems is 14 (North) or 15 (South/Southwest) as of 2023.",
      sourceName: "U.S. Department of Energy — HVAC Efficiency Standards 2023",
      sourceUrl: "https://www.energy.gov/articles/doe-updates-efficiency-standards-air-conditioners-and-heat-pumps",
      retrievedDate: "2026-07-02",
      standardCode: "DOE 10 CFR Part 430"
    },
    {
      question: "Should I repair or replace my HVAC system?",
      answer: "The industry rule is the '5,000 rule': multiply the system's age by the repair cost. If the result exceeds $5,000, replacement is more economical. For example, a 12-year-old system needing a $500 repair scores 6,000 — suggesting replacement. Additionally, if R-22 refrigerant (phased out since 2020) costs are involved, replacement is almost always preferred.",
      sourceName: "Air Conditioning Contractors of America (ACCA) — Repair vs. Replace Guide",
      sourceUrl: "https://www.acca.org",
      retrievedDate: "2026-07-02"
    },
    {
      question: "Are there federal tax credits available for a new HVAC system?",
      answer: "Yes. Under the Inflation Reduction Act, qualified homeowners can claim a 30% tax credit (up to $600) for high-efficiency central ACs, up to $2,000 for qualified heat pumps, and up to $1,200 annually for insulation and air sealing upgrades. The system must meet ENERGY STAR efficiency thresholds and be installed in your primary residence.",
      sourceName: "U.S. Department of Energy — Energy Efficient Home Improvement Credit (25C)",
      sourceUrl: "https://www.energy.gov/sites/default/files/2023-01/25C%20Home%20Efficiency%20Guide.pdf",
      retrievedDate: "2026-07-02",
      standardCode: "IRA Section 25C — HVAC credits"
    }
  ],

  roofing: [
    {
      question: "How long does a typical asphalt shingle roof last?",
      answer: "Three-tab asphalt shingles typically last 20–25 years under normal conditions. Architectural (dimensional) shingles, which are far more common in modern construction, have manufacturer warranties of 30–50 years and are rated for higher wind speeds. Actual lifespan depends on climate, attic ventilation, and installation quality. The NRCA estimates the average US roof is replaced every 20–25 years.",
      sourceName: "National Roofing Contractors Association (NRCA) — Roofing Materials Guide",
      sourceUrl: "https://www.nrca.net",
      retrievedDate: "2026-07-02"
    },
    {
      question: "Can a new roof be installed over the existing shingles (overlay)?",
      answer: "Most US building codes allow a maximum of two layers of asphalt shingles (IRC Section R905.1.1). Installing a third layer is prohibited and voids manufacturer warranties. While overlays cost roughly 25% less (no tear-off labor), they add 1,500–3,000 lbs to the structure, trap heat accelerating the new shingles' aging, and prevent inspection of the decking for rot or damage.",
      sourceName: "International Residential Code (IRC) — Section R905.1",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-02",
      standardCode: "IRC Section R905.1.1"
    },
    {
      question: "Is roof replacement covered by homeowners insurance?",
      answer: "Homeowners insurance generally covers sudden and accidental damage from wind, hail, fire, or fallen trees. It does not cover deterioration from age or lack of maintenance. Coverage depends on your policy (ACV vs. RCV): Actual Cash Value (ACV) pays the depreciated value of your old roof; Replacement Cost Value (RCV) pays the full cost to replace it. Always document storm damage promptly with photographs and file claims within 30–60 days of the event.",
      sourceName: "Insurance Information Institute (III) — Homeowners Insurance Guide",
      sourceUrl: "https://www.iii.org/article/homeowners-insurance-basics",
      retrievedDate: "2026-07-02"
    },
    {
      question: "How do I compare roofing contractor quotes?",
      answer: "Get a minimum of three written quotes covering: tear-off vs. overlay, decking repair (if needed), underlayment type, shingle brand and warranty, flashing replacement (valleys, pipes, chimney), and cleanup. Avoid contractors who offer unusually low bids, request full payment upfront, or pressure you after a storm. Verify contractor licensing and insurance, and check reviews on NRCA's Roofing Contractor Finder.",
      sourceName: "National Roofing Contractors Association (NRCA) — Hire a Roofing Contractor",
      sourceUrl: "https://www.nrca.net/homeowner/hire-a-contractor",
      retrievedDate: "2026-07-02"
    }
  ],

  kitchen: [
    {
      question: "What is the NKBA budget recommendation for a kitchen remodel?",
      answer: "The National Kitchen & Bath Association (NKBA) recommends spending 5–15% of your home's current market value on a kitchen remodel. On a $400,000 home, this translates to a budget of $20,000–$60,000. Spending below 5% typically results in cosmetic-only changes with minimal resale impact; spending above 15% risks 'over-improvement' where the investment exceeds what the local real estate market will support.",
      sourceName: "National Kitchen & Bath Association (NKBA) — Planning & Budgeting Guide",
      sourceUrl: "https://nkba.org/research",
      retrievedDate: "2026-07-02"
    },
    {
      question: "How much of a kitchen remodel cost is typically recovered at resale?",
      answer: "According to the NAR/NARI 2025 Remodeling Impact Report, a complete kitchen renovation averages a 60% cost recovery at resale. Minor kitchen upgrades (new appliances, cabinet refacing, countertops) also recover approximately 60% — making targeted mid-range renovations one of the best ROI strategies. Kitchens score a Joy Score of 10/10 for homeowner satisfaction.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      question: "Do I need permits for a kitchen remodel?",
      answer: "Permits are required for any work involving structural changes (removing walls), new or relocated electrical circuits (NEC 210.8 requires GFCI within 6 ft of the sink), and plumbing relocations. Cosmetic updates — painting, hardware, cabinet refacing, and appliance swaps — generally do not require permits. Always check with your local building department (AHJ), as requirements vary by jurisdiction.",
      sourceName: "International Residential Code (IRC) / National Electrical Code (NEC 210.8)",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-02",
      standardCode: "IRC Chapter 1 / NEC 210.8"
    },
    {
      question: "How long does a full kitchen remodel typically take?",
      answer: "A mid-range full kitchen remodel typically takes 6–12 weeks from demolition to final punch list. This breaks down as: 1–2 weeks demolition and rough work (plumbing, electrical, HVAC), 1 week cabinet installation, 1–2 weeks countertop templating and installation, 1–2 weeks tile and finish work, and 1 week appliance delivery and final trim. Custom cabinetry has a 6–10 week lead time, which is the most common schedule driver.",
      sourceName: "National Kitchen & Bath Association (NKBA) — Remodeling Timeline Guide",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-02"
    }
  ],

  bathroom: [
    {
      question: "What is the average cost of a full bathroom remodel in 2026?",
      answer: "Based on the NKBA 2026 Cost & Trends Report, the national median for a full bathroom remodel is approximately $35,000. A simple cosmetic update (new fixtures, paint, accessories) averages $8,000–$15,000. A luxury master bath with heated floors, custom tile, and high-end fixtures can exceed $75,000. Cost per square foot ranges from $115/sq ft (mid-range) to $575/sq ft (luxury) depending on finishes.",
      sourceName: "National Kitchen & Bath Association (NKBA) — 2026 Cost & Trends Report",
      sourceUrl: "https://nkba.org/research",
      retrievedDate: "2026-07-02"
    },
    {
      question: "Does bathroom remodeling require a permit?",
      answer: "Moving or adding plumbing, relocating walls, upgrading electrical circuits, or changing the layout of a bathroom requires permits in virtually all US jurisdictions. Under NEC 210.8(A)(1), all bathroom outlets must be GFCI-protected, and under IRC R303.3, a bathroom must have mechanical ventilation (50 CFM minimum exhaust fan) or openable windows. Cosmetic replacement of fixtures in the same location typically does not require permits.",
      sourceName: "National Electrical Code (NEC) / International Residential Code (IRC)",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-02",
      standardCode: "NEC 210.8(A)(1) / IRC Section R303.3"
    },
    {
      question: "What is the ROI on a bathroom remodel?",
      answer: "The NAR/NARI 2025 Remodeling Impact Report shows bathroom renovations recover approximately 50% of their cost at resale. However, the Joy Score of 9.8/10 makes it the highest-ranked interior project for homeowner satisfaction. A bathroom addition (adding a full bath to a home that only has one) can add 5% or more to overall home value, making it one of the highest ROI projects.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      question: "How do I waterproof a shower or wet area properly?",
      answer: "The TCNA and ANSI A108.01 require an impervious waterproof membrane behind all shower tile. Common methods include sheet-applied membranes (Schluter KERDI, RedGard), liquid-applied membranes, or cement backerboard with a bonded waterproofing layer. Tile backer board (cement board or foam) must extend at least 3 inches above the shower threshold and 12 inches above the top of the tub. Failures at corners, seams, and penetrations are the leading cause of water damage.",
      sourceName: "Tile Council of North America (TCNA) Handbook — W244/B415",
      sourceUrl: "https://www.tcnatile.com",
      retrievedDate: "2026-07-02",
      standardCode: "TCNA W244 / ANSI A108.01"
    }
  ],

  windows: [
    {
      question: "What is the difference between retrofit and full-frame window replacement?",
      answer: "A retrofit (insert) replacement slides a new window unit into the existing frame, preserving the original exterior trim and siding. This is less invasive and typically costs 25–35% less. A full-frame replacement removes everything down to the rough opening, allowing inspection and repair of the surrounding structure. Full-frame is necessary when frames are rotted, water-damaged, out of square, or when changing window size.",
      sourceName: "Window & Door Manufacturers Association (WDMA) — Installation Guide",
      sourceUrl: "https://www.wdma.com",
      retrievedDate: "2026-07-02"
    },
    {
      question: "How do I read the NFRC label on a window?",
      answer: "The NFRC (National Fenestration Rating Council) label lists U-factor (heat loss rate; lower is better, target ≤0.30 for most US climates), Solar Heat Gain Coefficient (SHGC; how much solar heat enters, lower is better in hot climates), Visible Transmittance (VT; how much light comes through), and Air Leakage (AL; lower is better). Use ENERGY STAR's online product finder to confirm which ratings qualify for your climate zone.",
      sourceName: "National Fenestration Rating Council (NFRC) — How to Read the Label",
      sourceUrl: "https://www.nfrc.org/ratings",
      retrievedDate: "2026-07-02",
      standardCode: "NFRC 100 / NFRC 200"
    },
    {
      question: "Do new windows qualify for a federal tax credit?",
      answer: "Yes. Under the IRA Energy Efficient Home Improvement Credit (Section 25C), ENERGY STAR Most Efficient windows qualify for a 30% tax credit up to $600 per year. Both windows and skylights are eligible. The credit is non-refundable and cannot exceed your federal tax liability for the year. Check the ENERGY STAR product finder to verify a specific window's certification before purchase.",
      sourceName: "U.S. Department of Energy — Energy Efficient Home Improvement Credit (25C)",
      sourceUrl: "https://www.energystar.gov/products/windows_doors_skylights/tax_credits",
      retrievedDate: "2026-07-02",
      standardCode: "IRA Section 25C — Window Credit"
    },
    {
      question: "How much do new windows reduce energy bills?",
      answer: "The U.S. Department of Energy estimates that replacing single-pane windows with ENERGY STAR certified windows saves $101–$583 per year in heating and cooling costs, depending on climate and window size. In cold climates, triple-pane windows with low U-factor (≤0.17) deliver the greatest savings. The payback period for new windows is typically 7–15 years through energy savings alone, making the resale ROI (74%) a key part of the financial case.",
      sourceName: "U.S. Department of Energy — Energy Efficient Windows",
      sourceUrl: "https://www.energy.gov/energysaver/update-or-replace-windows",
      retrievedDate: "2026-07-02"
    }
  ],

  deck: [
    {
      question: "Do I need a permit to build a deck?",
      answer: "In nearly all US jurisdictions, a permit is required for any deck that is attached to the house or elevated more than 30 inches above grade (IRC Section R507). Permit applications require structural drawings showing footing size, post spacing, beam span, and ledger attachment details. NADRA reports that a 2009 deck collapse in Glendale, AZ that killed 2 people led to significant reinforcement of the IRC 2009 deck-specific requirements.",
      sourceName: "International Residential Code (IRC) — Section R507 Decks",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-02",
      standardCode: "IRC Section R507"
    },
    {
      question: "What is the best decking material — wood, composite, or hardwood?",
      answer: "Pressure-treated pine is the most affordable choice ($40–$50/sq ft installed) but requires annual sealing or staining. Composite decking (Trex, TimberTech) costs $55–$70/sq ft installed but never needs staining and carries 25–30 year warranties. Tropical hardwoods like Ipe or Tigerwood run $75–$100/sq ft but are incredibly dense, naturally rot-resistant, and can last 50+ years with minimal care. The right choice depends on your budget, maintenance tolerance, and aesthetic goals.",
      sourceName: "North American Deck and Railing Association (NADRA) — Decking Material Guide",
      sourceUrl: "https://www.nadra.org",
      retrievedDate: "2026-07-02"
    },
    {
      question: "How deep do deck footings need to be?",
      answer: "Deck footings must extend below the local frost line to prevent frost heave from shifting the deck. Frost line depths range from 0 inches in Florida to 48+ inches in Minnesota and Maine. The minimum footing diameter for most residential decks is 10–12 inches and must rest on undisturbed native soil. The USDA provides a frost depth map searchable by zip code, and local building departments specify the exact required depth.",
      sourceName: "International Residential Code (IRC) — Section R403.1.4",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-02",
      standardCode: "IRC Section R403.1.4"
    },
    {
      question: "What are the code requirements for deck railings and balusters?",
      answer: "Under IRC Section R312, guard rails are required for any deck surface more than 30 inches above grade. Rail height must be at least 36 inches for decks under 30 inches, and 42 inches for decks 30 inches or higher. Balusters must be spaced no more than 4 inches apart (a 4-inch sphere must not pass through). Horizontal rail designs that can be used as a ladder are prohibited.",
      sourceName: "International Residential Code (IRC) — Section R312 Guards",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-02",
      standardCode: "IRC Section R312"
    }
  ],

  "water-heater": [
    {
      question: "How fast should I replace a leaking water heater?",
      answer: "A tank that is leaking from the body usually needs replacement, not repair. Shut off the water supply, turn off gas or electrical power, and schedule replacement quickly because a failed tank can release dozens of gallons of water. Emergency service, code upgrades, venting, and pan/drain work are the main cost drivers.",
      sourceName: "U.S. Department of Energy — Water Heating",
      sourceUrl: "https://www.energy.gov/energysaver/water-heating",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Do heat pump water heaters still qualify for a federal tax credit?",
      answer: "Qualifying electric or natural gas heat pump water heaters can qualify for the Energy Efficient Home Improvement Credit: 30% of qualifying expenses up to $2,000 per year. The credit is nonrefundable, limited by tax liability, and tied to qualifying property rules, so homeowners should verify current IRS requirements before purchase.",
      sourceName: "Internal Revenue Service — Energy Efficient Home Improvement Credit",
      sourceUrl: "https://www.irs.gov/credits-deductions/energy-efficient-home-improvement-credit",
      retrievedDate: "2026-07-07",
      standardCode: "IRC Section 25C"
    },
    {
      question: "Should I choose tank, tankless, or heat pump?",
      answer: "A standard tank is usually the fastest like-for-like replacement. Tankless units save space but often require gas line and venting upgrades. Heat pump water heaters use electricity efficiently and can lower operating cost, but they need adequate room air volume, condensate drainage, and sometimes electrical work.",
      sourceName: "U.S. Department of Energy — Selecting a New Water Heater",
      sourceUrl: "https://www.energy.gov/energysaver/selecting-new-water-heater",
      retrievedDate: "2026-07-07"
    }
  ],

  "roof-shingles": [
    {
      question: "How many bundles of shingles are in one roofing square?",
      answer: "One roofing square equals 100 square feet. Many standard three-tab and architectural asphalt shingles require 3 bundles per square, while some heavier designer shingles require 4 or more. Always verify the coverage printed on the shingle wrapper before ordering.",
      sourceName: "National Roofing Contractors Association (NRCA) — Roofing Glossary",
      sourceUrl: "https://www.nrca.net",
      retrievedDate: "2026-07-07"
    },
    {
      question: "How much shingle waste should I add?",
      answer: "Simple gable roofs usually need about 10% waste. Roofs with valleys, dormers, skylights, hips, and cut-up geometry should use 15% to 20%. Starter strips, ridge caps, and field shingles should be counted separately because their bundle coverage differs.",
      sourceName: "Asphalt Roofing Manufacturers Association (ARMA)",
      sourceUrl: "https://www.asphaltroofing.org",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Do asphalt shingles require underlayment?",
      answer: "Yes. The IRC requires underlayment beneath asphalt shingles, and many cold-climate jurisdictions require an ice barrier at eaves. Underlayment, drip edge, flashing, starter strips, and ridge caps are part of a complete roof assembly, not optional accessories.",
      sourceName: "International Residential Code — Roof Assemblies",
      sourceUrl: "https://codes.iccsafe.org/content/IRC2024P1/chapter-9-roof-assemblies",
      retrievedDate: "2026-07-07",
      standardCode: "IRC R905.2.3"
    }
  ],

  siding: [
    {
      question: "Should house wrap be replaced when installing new siding?",
      answer: "Usually yes. DOE Building America guidance treats the drainage plane behind cladding as a critical water-management layer. If the old house wrap is torn, reverse-lapped, unsealed, or missing around penetrations, it should be corrected before new siding covers the wall.",
      sourceName: "DOE Building America Solution Center — Drainage Plane Behind Exterior Wall Cladding",
      sourceUrl: "https://basc.pnnl.gov/resource-guides/drainage-plane-behind-exterior-wall-cladding",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Why does siding cost more on two-story homes?",
      answer: "Multi-story siding jobs require more staging, ladder work, pump jacks or scaffolding, longer material handling, and additional safety setup. Openings, corners, gables, dormers, and trim profiles also slow production and increase labor cost.",
      sourceName: "Polymeric Exterior Products Association — Vinyl Siding Installation Manual",
      sourceUrl: "https://polymericexteriors.org/installation/installation-manual/",
      retrievedDate: "2026-07-07"
    },
    {
      question: "What siding material has the best ROI?",
      answer: "Vinyl and fiber cement are commonly chosen because they balance installed cost, durability, and broad buyer acceptance. Brick veneer and premium claddings can look excellent but may over-improve lower-priced homes unless the local market supports the upgrade.",
      sourceName: "National Association of Realtors — Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-07"
    }
  ],

  driveway: [
    {
      question: "Is asphalt, concrete, gravel, or pavers cheapest?",
      answer: "Gravel is usually the lowest upfront cost, asphalt is a common mid-cost paved option, concrete costs more but has a longer service life, and pavers are typically the most expensive because they require a compacted base and hand-set units. Drainage and base prep can change the final price more than material choice.",
      sourceName: "Federal Highway Administration — Gravel Roads Construction & Maintenance Guide",
      sourceUrl: "https://www.fhwa.dot.gov/construction/pubs/ots15002.pdf",
      retrievedDate: "2026-07-07"
    },
    {
      question: "How thick should a concrete driveway be?",
      answer: "A 4-inch concrete slab is typical for passenger vehicles when placed over a stable, compacted base. Heavier trucks, RVs, poor soil, or freeze-thaw exposure may require thicker concrete, reinforcement, or more aggregate base. Local contractors should verify subgrade conditions before paving.",
      sourceName: "Portland Cement Association — Concrete Driveways",
      sourceUrl: "https://www.cement.org",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Do I need to call 811 before replacing a driveway?",
      answer: "Yes. Driveway demolition and excavation can disturb buried utilities, irrigation lines, gas lines, electrical conduits, and telecom service. Call 811 before digging so utilities can mark underground lines before demolition or base excavation starts.",
      sourceName: "U.S. Common Ground Alliance — Call 811",
      sourceUrl: "https://call811.com",
      retrievedDate: "2026-07-07"
    }
  ],

  "home-addition": [
    {
      question: "How much does a room addition cost per square foot?",
      answer: "National room addition budgets commonly range from about $200 to $450+ per square foot depending on room type, plumbing, HVAC, foundation, finish level, and structural tie-in complexity. Kitchens, bathrooms, second stories, and additions over poor soil usually cost more than simple bedrooms or family rooms.",
      sourceName: "Harvard Joint Center for Housing Studies — Remodeling Research",
      sourceUrl: "https://www.jchs.harvard.edu/research-areas/remodeling",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Does a home addition require permits?",
      answer: "Yes. Additions almost always require zoning approval, building permits, inspections, and trade permits for electrical, plumbing, HVAC, and structural work. Bedrooms must satisfy emergency escape and rescue opening rules, smoke/CO alarm requirements, and local energy-code requirements.",
      sourceName: "International Residential Code — Emergency Escape and Rescue Openings",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-07",
      standardCode: "IRC R310"
    },
    {
      question: "Is it cheaper to build out or build up?",
      answer: "Building out adds foundation, excavation, drainage, and site work. Building up can avoid new foundation area but often requires structural reinforcement, stair changes, roof removal, and more disruption. The cheaper option depends on lot setbacks, existing foundation capacity, roof design, and local labor.",
      sourceName: "International Residential Code — Foundations and Structural Provisions",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-07"
    }
  ],

  roi: [
    {
      question: "What home improvement has the highest ROI at resale?",
      answer: "According to the NAR/NARI 2025 Remodeling Impact Report, a new steel front door has the highest cost recovery at 100%, meaning you recoup your full investment at resale. Closet renovations recover 83%, and fiberglass entry doors recover 80%. For larger projects, roof replacement and kitchen upgrades both recover approximately 60%, while bathroom renovations recover about 50%.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02",
      standardCode: "NAR 2025 Remodeling Impact Report"
    },
    {
      question: "What is the Joy Score in the NAR Remodeling Impact Report?",
      answer: "The Joy Score is a 1–10 scale measuring how happy homeowners feel in their home after completing a remodeling project, separate from the financial ROI question. Projects with perfect Joy Scores of 10 include primary suite additions, complete kitchen renovations, and roof replacements. Even projects with modest financial ROI (like bathroom renovations at 50%) earn high Joy Scores (9.8), suggesting strong quality-of-life benefits.",
      sourceName: "National Association of Realtors (NAR) — 2025 Remodeling Impact Report Methodology",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      question: "What does 'over-improvement' mean and how do I avoid it?",
      answer: "Over-improvement occurs when you invest more in a renovation than the local real estate market will reward. Appraisers use comparable sales (comps) — not renovation costs — to determine home value. If your home is worth $250,000 and the best homes in your neighborhood sell for $325,000, spending $200,000 on a remodel won't push your home's value above the neighborhood ceiling. The NAR recommends keeping any single project within 15% of your home's current market value.",
      sourceName: "National Association of Realtors (NAR) — Home Improvements & Value",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    },
    {
      question: "Should I remodel before selling or sell as-is?",
      answer: "The answer depends on market conditions and project type. High-ROI, fast projects like fresh paint ($4,800, ~55% recovery), a new front door ($2,300, ~100% recovery), or landscaping improvements typically pay off before listing. Larger renovations (full kitchen, bathroom addition) take 3–6 months and may not close before you need to list. In a seller's market, selling as-is and letting buyers renovate to their taste is often more practical and financially sound.",
      sourceName: "National Association of Realtors (NAR) / NARI — 2025 Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-02"
    }
  ],

  pool: [
    {
      question: "What costs are usually missing from a pool quote?",
      answer: "Pool quotes can exclude fencing, electrical upgrades, gas lines, retaining walls, extra decking, landscaping repair, drainage, alarms, and permit fees. Compare bids by full scope, not just shell price. Safety barriers and self-closing gates are especially important because many jurisdictions require them before a pool can pass final inspection.",
      sourceName: "U.S. Consumer Product Safety Commission — Pool Safely",
      sourceUrl: "https://www.poolsafely.gov",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Is fiberglass, vinyl liner, or gunite cheapest?",
      answer: "Vinyl-liner pools usually have the lowest installed cost among in-ground pools, fiberglass is often faster to install with a mid-to-high upfront cost, and concrete or gunite is the most customizable but typically the most expensive. Site access, retaining walls, decking, and equipment can move the total more than shell material alone.",
      sourceName: "U.S. Department of Energy — Swimming Pool Heating",
      sourceUrl: "https://www.energy.gov/energysaver/swimming-pool-heating",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Should I add a pool heater?",
      answer: "A heater extends the swimming season, but it adds equipment cost and ongoing energy cost. DOE guidance recommends using pool covers to reduce heat loss and considering pump efficiency, heater type, local climate, and hours of use before committing to a heated pool.",
      sourceName: "U.S. Department of Energy — Swimming Pool Covers",
      sourceUrl: "https://www.energy.gov/energysaver/swimming-pool-covers",
      retrievedDate: "2026-07-07"
    }
  ],

  countertop: [
    {
      question: "How do I measure countertop square footage?",
      answer: "Measure each countertop section as length times depth, then divide square inches by 144 to get square feet. Include islands, peninsulas, finished ends, and bar tops. Fabricators will still make a field template before cutting stone or solid-surface slabs, so homeowner measurements are for budgeting only.",
      sourceName: "National Kitchen & Bath Association — Kitchen Planning Guidelines",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Why are quartz and stone quotes so different between contractors?",
      answer: "Stone quotes depend on slab grade, slab count, seam layout, edge profile, cutouts, sink mounting, backsplash, demolition, and whether plumbing reconnection is included. A cheaper quote may exclude tear-out, disposal, faucet holes, cooktop cutouts, or trip charges for templating and installation.",
      sourceName: "Occupational Safety and Health Administration — Respirable Crystalline Silica",
      sourceUrl: "https://www.osha.gov/silica-crystalline",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Do I need new cabinets before replacing countertops?",
      answer: "Cabinets must be level, secure, and strong enough to support the new material before templating. Heavy stone on weak or uneven cabinets can crack seams or create installation problems. If cabinets are being replaced, countertops should be templated after the cabinets are fully installed.",
      sourceName: "National Kitchen & Bath Association — Kitchen Installation Practices",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-07"
    }
  ],

  gutters: [
    {
      question: "How many downspouts does my house need?",
      answer: "Downspout count depends on roof drainage area, rainfall intensity, gutter size, slope, and outlet size. As a budgeting rule, long roof runs need more outlets to prevent overflow. A contractor should size the system so water leaves the roof edge and discharges away from the foundation.",
      sourceName: "Sheet Metal and Air Conditioning Contractors' National Association — Architectural Sheet Metal Manual",
      sourceUrl: "https://www.smacna.org",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Are gutter guards worth it?",
      answer: "Gutter guards can reduce cleaning frequency where trees overhang the roof, but they do not eliminate maintenance. Micro-mesh systems cost more than screens or foam inserts but usually block smaller debris better. They are most cost-effective when installed during gutter replacement because labor overlaps.",
      sourceName: "U.S. Environmental Protection Agency — Soak Up the Rain",
      sourceUrl: "https://www.epa.gov/soakuptherain/soak-rain-downspout-disconnection",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Why repair fascia before installing gutters?",
      answer: "Gutters attach to fascia boards. If the fascia is soft, rotted, or pulling away, new gutters can sag, leak, or detach under water and ice load. Replacement quotes often reveal fascia damage only after old gutters come down, so carrying a repair allowance is prudent.",
      sourceName: "DOE Building America Solution Center — Water Management",
      sourceUrl: "https://basc.pnnl.gov",
      retrievedDate: "2026-07-07"
    }
  ],

  "basement-finishing": [
    {
      question: "Can every basement be finished?",
      answer: "No. A basement should be dry, structurally sound, and able to meet local ceiling-height, egress, ventilation, electrical, and fire-safety requirements before it is finished. Chronic water intrusion should be corrected before insulation, drywall, flooring, or built-ins are installed.",
      sourceName: "U.S. Environmental Protection Agency — Mold Resources",
      sourceUrl: "https://www.epa.gov/mold",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Does a basement bedroom need an egress window?",
      answer: "Under the IRC, basement sleeping rooms generally need an emergency escape and rescue opening. Window size, sill height, well dimensions, and ladder requirements all matter. Local amendments can vary, so legal-bedroom status should be verified before advertising or appraising the space as a bedroom.",
      sourceName: "International Residential Code — Emergency Escape and Rescue Openings",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-07",
      standardCode: "IRC R310"
    },
    {
      question: "What adds the most cost to a basement finishing project?",
      answer: "Bathrooms, egress windows, moisture correction, low ceilings, electrical panel upgrades, plumbing pump systems, and premium flooring or built-ins are the main cost escalators. A simple recreation room costs far less than a legal bedroom suite with a full bath.",
      sourceName: "National Association of Realtors / NARI — Remodeling Impact Report",
      sourceUrl: "https://www.nar.realtor/reports/remodeling-impact",
      retrievedDate: "2026-07-07"
    }
  ],

  insulation: [
    {
      question: "What R-value should attic insulation reach?",
      answer: "DOE and ENERGY STAR recommendations vary by climate zone. Many southern homes target roughly R-30 to R-38, mixed climates often target R-38 to R-49, and colder northern zones commonly target R-49 to R-60. The target is total R-value after counting any existing insulation that is dry and not compressed.",
      sourceName: "ENERGY STAR — Recommended Home Insulation R-Values",
      sourceUrl: "https://www.energystar.gov/saveathome/seal_insulate/identify-problems-you-want-fix/diy-checks-inspections/insulation-r-values",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Does insulation still qualify for the federal 25C credit?",
      answer: "For 2026 budgeting, this calculator assumes no federal 25C insulation credit. Public Law 119-21 terminated the Energy Efficient Home Improvement Credit for property placed in service after 2025. State, utility, or local rebates may still exist and should be checked separately.",
      sourceName: "U.S. Congress / GovInfo — Public Law 119-21",
      sourceUrl: "https://www.govinfo.gov/app/details/PLAW-119publ21",
      retrievedDate: "2026-07-07",
      standardCode: "IRC Section 25C"
    },
    {
      question: "Should I air seal before adding insulation?",
      answer: "Yes. Air leaks around top plates, plumbing penetrations, recessed lights, attic hatches, and duct chases can bypass insulation. DOE guidance recommends sealing leaks before adding insulation so the new R-value can perform as intended and humid indoor air does not move into attic assemblies.",
      sourceName: "U.S. Department of Energy — Air Sealing Your Home",
      sourceUrl: "https://www.energy.gov/energysaver/air-sealing-your-home",
      retrievedDate: "2026-07-07"
    }
  ],

  "cabinet-refacing": [
    {
      question: "Is cabinet refacing cheaper than replacement?",
      answer: "Usually, yes. Refacing keeps the cabinet boxes and changes doors, drawer fronts, skins, and hardware. Replacement costs more because demolition, box installation, layout fixes, countertop risk, plumbing, and electrical coordination can enter the scope.",
      sourceName: "National Kitchen & Bath Association — Kitchen Planning Guidelines",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-07"
    },
    {
      question: "When should I replace cabinets instead of refacing them?",
      answer: "Replace cabinets when boxes are water damaged, swollen, out of square, poorly laid out, or structurally weak. Refacing does not fix bad storage layout or failing cabinet boxes; it is best for solid cabinets with dated doors and finishes.",
      sourceName: "Federal Trade Commission — Hiring a Contractor",
      sourceUrl: "https://consumer.ftc.gov/articles/hiring-contractor",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Can I keep my existing countertops during cabinet work?",
      answer: "Sometimes. Refacing normally leaves countertops alone. Full cabinet replacement can crack or require removal of existing countertops, especially stone, because the cabinet boxes beneath them must be removed and reset level.",
      sourceName: "National Kitchen & Bath Association — Kitchen Installation Practices",
      sourceUrl: "https://nkba.org",
      retrievedDate: "2026-07-07"
    }
  ],

  "backup-generator": [
    {
      question: "How many watts does a home backup generator need?",
      answer: "Add the running watts for essential loads, then include starting surge for motor loads such as pumps, refrigerators, blowers, and air conditioners. Whole-home standby systems often land far above portable units because central AC and electric heat can dominate the load.",
      sourceName: "Ready.gov — Power Outages",
      sourceUrl: "https://www.ready.gov/power-outages",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Can I plug a generator into a wall outlet?",
      answer: "No. Backfeeding a house through an outlet can energize utility lines and injure utility workers or neighbors. A generator connected to home wiring needs a proper transfer switch or interlock installed under local electrical code.",
      sourceName: "National Fire Protection Association — National Electrical Code",
      sourceUrl: "https://www.nfpa.org/codes-and-standards/nfpa-70-standard-development/70",
      retrievedDate: "2026-07-07",
      standardCode: "NEC Article 702"
    },
    {
      question: "Where should a portable generator be placed?",
      answer: "Run portable generators outdoors only, away from windows, doors, vents, garages, basements, and crawlspaces. Carbon monoxide can build up quickly indoors or near openings, so CO alarms should be working before storm season.",
      sourceName: "U.S. Consumer Product Safety Commission — Generators and Engine-Driven Tools",
      sourceUrl: "https://www.cpsc.gov/Safety-Education/Safety-Education-Centers/Carbon-Monoxide-Information-Center/Generators-and-Engine-Driven-Tools",
      retrievedDate: "2026-07-07"
    }
  ],

  "ev-charger": [
    {
      question: "How fast is a Level 2 home EV charger?",
      answer: "DOE AFDC lists Level 2 charging at roughly 25 miles of range per hour under typical assumptions, with equipment power ranging from about 2.9 to 19.2 kW. Actual speed depends on vehicle onboard charger capacity, circuit size, battery state, and charger settings.",
      sourceName: "U.S. Department of Energy AFDC — Electric Vehicle Charging Stations",
      sourceUrl: "https://afdc.energy.gov/fuels/electricity-stations",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Does the federal EV charger tax credit still apply?",
      answer: "For this 2026 calculator, the federal Section 30C credit is treated as expired after June 30, 2026. Homeowners should still check state, utility, and local programs because charger rebates can exist outside federal tax law.",
      sourceName: "Internal Revenue Service — About Form 8911",
      sourceUrl: "https://www.irs.gov/forms-pubs/about-form-8911",
      retrievedDate: "2026-07-07",
      standardCode: "IRC Section 30C"
    },
    {
      question: "Why does panel work make EV charger installation expensive?",
      answer: "A Level 2 charger is a large continuous electrical load. If the panel is full or the service load calculation is tight, the project may need load management, a subpanel, or a service upgrade before the charger can be installed safely.",
      sourceName: "National Fire Protection Association — National Electrical Code",
      sourceUrl: "https://www.nfpa.org/codes-and-standards/nfpa-70-standard-development/70",
      retrievedDate: "2026-07-07",
      standardCode: "NEC Article 625"
    }
  ],

  "siding-material": [
    {
      question: "How much siding waste should I add?",
      answer: "Use about 10% waste for simple rectangular walls, 15% for typical homes with gables and openings, and 20% for complex elevations with dormers, short returns, or many trim cuts. Ordering too tightly can leave you short on matching lots.",
      sourceName: "Polymeric Exterior Products Association — Vinyl Siding Installation Manual",
      sourceUrl: "https://polymericexteriors.org/installation/installation-manual/",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Should I subtract windows and doors?",
      answer: "Subtract large openings if you know their area, but keep enough waste for cuts, trim returns, and mistakes. Many homeowners subtract garage doors and large patio doors but leave small windows in the gross wall area for a safer order.",
      sourceName: "DOE Building America Solution Center — Drainage Plane Behind Exterior Wall Cladding",
      sourceUrl: "https://basc.pnnl.gov/resource-guides/drainage-plane-behind-exterior-wall-cladding",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Do I need house wrap under new siding?",
      answer: "A continuous drainage plane or weather-resistive barrier is part of a durable siding assembly. New siding is also the right time to repair flashing and house wrap around windows, doors, and penetrations.",
      sourceName: "DOE Building America Solution Center",
      sourceUrl: "https://basc.pnnl.gov",
      retrievedDate: "2026-07-07"
    }
  ],

  "retaining-wall": [
    {
      question: "When does a retaining wall need engineering?",
      answer: "Many jurisdictions require permit or engineering review around 4 feet of retained height, and sometimes lower when the wall supports a driveway, building, fence, steep slope, or other surcharge. Always verify local rules before excavating.",
      sourceName: "International Residential Code / local building departments",
      sourceUrl: "https://codes.iccsafe.org",
      retrievedDate: "2026-07-07",
      standardCode: "IRC R404 / local amendments"
    },
    {
      question: "Why is drainage so important behind retaining walls?",
      answer: "Water trapped behind a wall adds pressure and can cause bulging, rotation, or failure. Drain stone, perforated pipe, filter fabric, and proper outlet points are often more important than the visible block face.",
      sourceName: "National Concrete Masonry Association — Segmental Retaining Wall Best Practices",
      sourceUrl: "https://ncma.org",
      retrievedDate: "2026-07-07"
    },
    {
      question: "Can I build a retaining wall myself?",
      answer: "Small landscape walls are often DIY-friendly when soil is stable and drainage is simple. Tall walls, poor soils, slopes, nearby structures, or vehicle loads should be designed and installed by experienced professionals.",
      sourceName: "U.S. Common Ground Alliance — Call 811",
      sourceUrl: "https://call811.com",
      retrievedDate: "2026-07-07"
    }
  ]
};
